// skills/fishing/fishing.js
// Fishing skill logic

import { FISH, RODS } from './fishingData.js';
import { gameState } from '../../game/gameState.js';
import { addXP } from '../../systems/experience.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { awardDrops, canFitItem } from '../../systems/drops.js';
import { rollForCorruption } from '../../systems/corruption.js';
import { pilferingSkill } from '../pilfering/pilfering.js';
import { bountySkill } from '../bounty/bounty.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import {
    getTalentLootBonus,
    getTalentFlatBonus,
    rollTalentProc,
    incrementStackCount,
    resetStackCount,
    rollGlobalRareLoot
} from '../../systems/talentTree/talentTree.js';
import { FOOD } from '../cooking/cookingData.js';
import { recordTalentProc } from '../../ui/talentActivity.js';
import { BaseSkill } from '../../core/BaseSkill.js';

// Raw fish to cooked fish mapping for auto-cook talent
const RAW_TO_COOKED = {
    'raw_sand_smelt': { cooked: 'cooked_sand_smelt', cookingXp: 30 },
    'raw_silver_sprat': { cooked: 'cooked_silver_sprat', cookingXp: 40 },
    'raw_blueback': { cooked: 'cooked_blueback', cookingXp: 50 },
    'raw_river_char': { cooked: 'cooked_river_char', cookingXp: 70 },
    'raw_crimson_char': { cooked: 'cooked_crimson_char', cookingXp: 90 },
    'raw_skipjack': { cooked: 'cooked_skipjack', cookingXp: 100 },
    'raw_spiny_lobster': { cooked: 'cooked_spiny_lobster', cookingXp: 120 },
    'raw_broadbill': { cooked: 'cooked_broadbill', cookingXp: 140 },
    'raw_mako': { cooked: 'cooked_mako', cookingXp: 210 }
};

class FishingSkill extends BaseSkill {
    constructor() {
        super(SKILLS.FISHING, RODS, 'BASIC');
        // Track instant catch proc (Perfect Cast talent)
        this.nextCatchInstant = false;
        // Track guaranteed double (Feeding Frenzy talent)
        this.nextCatchDouble = false;
    }

    /**
     * Get the currently equipped rod object (alias for BaseSkill.getCurrentTool)
     */
    get currentRod() {
        return this.getCurrentTool();
    }

    /**
     * Roll for talent proc effects and apply them
     * Returns object with proc results
     */
    rollFishingProcs(fish) {
        const procs = {
            doubleFish: false,
            bycatch: null,
            autoCook: false,
            luckyHaul: false
        };

        // Check for guaranteed double from Feeding Frenzy
        if (this.nextCatchDouble) {
            this.nextCatchDouble = false;
            procs.doubleFish = true;
            procs.feedingFrenzyUsed = true; // Mark that this double came from Feeding Frenzy
        }
        // Lucky Haul - 8% chance to double ALL drops (higher priority)
        else if (rollTalentProc(SKILLS.FISHING, 'luckyHaul')) {
            procs.luckyHaul = true;
            procs.doubleFish = true; // Also triggers double fish
        }
        // Double fish - 5% per rank (Well Hooked)
        else if (rollTalentProc(SKILLS.FISHING, 'doubleFish')) {
            procs.doubleFish = true;
        }

        // Bycatch - 4% per rank - find bonus items
        if (rollTalentProc(SKILLS.FISHING, 'bycatch')) {
            procs.bycatch = this.getBycatchItem();
            // Lucky Haul doubles bycatch too
            if (procs.luckyHaul) {
                procs.bycatchDouble = true;
            }
        }

        // Auto-cook - 10% (Fresh Preparation) + 15% (Flame-Kissed Catch)
        if (rollTalentProc(SKILLS.FISHING, 'autoCook')) {
            procs.autoCook = true;
        }

        return procs;
    }

    /**
     * Get a random bycatch item with weighted tiers
     * Common: low tier seeds/herbs, Uncommon: mid tier, Rare: high tier + rare bones
     */
    getBycatchItem() {
        const bycatchTable = [
            // Common tier (weight 50) - low level seeds and herbs
            { itemId: 'potato_seed', weight: 8 },
            { itemId: 'onion_seed', weight: 8 },
            { itemId: 'cabbage_seed', weight: 6 },
            { itemId: 'greenleaf_seed', weight: 6 },
            { itemId: 'duskleaf_seed', weight: 5 },
            { itemId: 'grimy_greenleaf', weight: 6 },
            { itemId: 'grimy_duskleaf', weight: 5 },
            { itemId: 'small_bone', weight: 6 },

            // Uncommon tier (weight 30) - mid level seeds and herbs
            { itemId: 'tomato_seed', weight: 4 },
            { itemId: 'corn_seed', weight: 4 },
            { itemId: 'ironbloom_seed', weight: 3 },
            { itemId: 'emberroot_seed', weight: 3 },
            { itemId: 'ironwood_seed', weight: 3 },
            { itemId: 'softbark_seed', weight: 3 },
            { itemId: 'grimy_ironbloom', weight: 3 },
            { itemId: 'grimy_emberroot', weight: 3 },
            { itemId: 'grimy_sanctum_leaf', weight: 2 },
            { itemId: 'charred_bone', weight: 2 },

            // Rare tier (weight 15) - high level seeds, herbs, rare bones
            { itemId: 'strawberry_seed', weight: 2 },
            { itemId: 'watermelon_seed', weight: 1 },
            { itemId: 'sunpetal_seed', weight: 2 },
            { itemId: 'deepvine_seed', weight: 1 },
            { itemId: 'goldleaf_seed', weight: 2 },
            { itemId: 'redgrain_seed', weight: 1 },
            { itemId: 'grimy_sunpetal', weight: 2 },
            { itemId: 'grimy_deepvine', weight: 1 },
            { itemId: 'grimy_stonebark', weight: 1 },
            { itemId: 'dragon_bone', weight: 2 }
        ];

        const totalWeight = bycatchTable.reduce((sum, item) => sum + item.weight, 0);
        let roll = Math.random() * totalWeight;

        for (const item of bycatchTable) {
            roll -= item.weight;
            if (roll <= 0) {
                return item.itemId;
            }
        }

        // Fallback
        return 'potato_seed';
    }

    /**
     * Get a random treasure item (gold or gems)
     */
    getTreasureItem() {
        const treasureItems = [
            { itemId: 'coins', minQty: 10, maxQty: 50 },
            { itemId: 'coins', minQty: 25, maxQty: 100 },
            { itemId: 'ruby', minQty: 1, maxQty: 1 },
            { itemId: 'emerald', minQty: 1, maxQty: 1 },
            { itemId: 'sapphire', minQty: 1, maxQty: 1 }
        ];
        const item = treasureItems[Math.floor(Math.random() * treasureItems.length)];
        const qty = Math.floor(Math.random() * (item.maxQty - item.minQty + 1)) + item.minQty;
        return { itemId: item.itemId, quantity: qty };
    }

    /**
     * Check if player can fish
     */
    canFish(fishId) {
        // Find fish by id property, not by object key
        const fish = Object.values(FISH).find(f => f.id === fishId);
        if (!fish) return { success: false, reason: 'Fish not found' };

        const skill = gameState.getSkill(SKILLS.FISHING);
        if (skill.level < fish.level) {
            return { success: false, reason: `Requires level ${fish.level} Fishing` };
        }

        // Check if primary drop can fit in inventory
        const primaryDrop = fish.drops[0];
        if (primaryDrop && !canFitItem(primaryDrop.itemId)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    /**
     * Start fishing
     */
    startFishing(fishId) {
        // Find fish by id property, not by object key
        const fish = Object.values(FISH).find(f => f.id === fishId);
        const canFish = this.canFish(fishId);

        if (!canFish.success) {
            logger.debug('Cannot fish:', canFish.reason);
            return false;
        }

        // Check for instant catch (Perfect Cast talent proc from previous catch)
        if (this.nextCatchInstant) {
            this.nextCatchInstant = false;
            const result = this.completeFishing(fish);
            if (result.inventoryFull) {
                toastWarning('Inventory full - fishing stopped');
                return false;
            }
            // Start next catch normally (could chain into another instant)
            return this.startFishing(fishId);
        }

        // Calculate fishing time including talent bonuses
        const baseTime = fish.baseTime;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const fishingTime = baseTime / speedMultiplier;

        // Create fishing action
        const action = {
            type: 'fishing',
            name: `Fishing ${fish.name}`,
            fish: fish,
            fishId: fishId.toUpperCase(),
            progress: 0,
            duration: fishingTime,
            startTime: Date.now(),
            forceComplete: false,

            // Tick function called every game tick
            // speedMultiplier param includes admin speed + corruption (applied in tick.js)
            tick: (speedMultiplier = 1) => {
                // Check for force complete (used by admin panel)
                if (action.forceComplete) {
                    action.forceComplete = false;
                    const result = this.completeFishing(fish);
                    if (result.inventoryFull) {
                        toastWarning('Inventory full - fishing stopped');
                        gameState.clearAction();
                        return;
                    }
                    const canContinue = this.canFish(fishId);
                    if (canContinue.success) {
                        this.startFishing(fishId);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                // Apply speed multiplier to elapsed time
                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                // Complete when progress reaches 1
                if (action.progress >= 1) {
                    const result = this.completeFishing(fish);
                    if (result.inventoryFull) {
                        toastWarning('Inventory full - fishing stopped');
                        gameState.clearAction();
                        return;
                    }
                    const canContinue = this.canFish(fishId);
                    if (canContinue.success) {
                        this.startFishing(fishId);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    /**
     * Complete a fishing action
     * @returns {{ inventoryFull: boolean }} Whether inventory became full
     */
    completeFishing(fish) {
        // Roll for talent procs first
        const procs = this.rollFishingProcs(fish);

        // Award Fishing XP (pass fish.id for admin node XP override)
        const xpResult = addXP(SKILLS.FISHING, fish.xp, fish.id);

        // Determine if fish should be auto-cooked
        let dropsToAward = fish.drops;
        let autoCookDoubled = false;
        const primaryFishId = fish.drops[0]?.itemId;
        if (procs.autoCook) {
            const rawFishId = fish.drops[0]?.itemId;
            const cookingInfo = RAW_TO_COOKED[rawFishId];
            if (cookingInfo) {
                // Replace raw fish with cooked fish
                dropsToAward = [{ itemId: cookingInfo.cooked, quantity: 1 }];
                // Award Cooking XP
                addXP(SKILLS.COOKING, cookingInfo.cookingXp, fish.id, { skipCorruption: true });
                recordTalentProc(SKILLS.FISHING, 'Fresh Preparation', null, null, { itemId: cookingInfo.cooked, quantity: 1 });

                // Flame-Kissed Mastery: Cooking talent allows double food proc on auto-cooked fish
                const hasFlameKissedMastery = getTalentFlatBonus(SKILLS.COOKING, 'flameKissedMastery') > 0;
                if (hasFlameKissedMastery && rollTalentProc(SKILLS.COOKING, 'doubleFood')) {
                    autoCookDoubled = true;
                    recordTalentProc(SKILLS.COOKING, 'Flame-Kissed Mastery', null, null, { itemId: cookingInfo.cooked, quantity: 2 });
                }
            }
        }

        // Award drops (handles corruption loot multiplier centrally)
        const dropResult = awardDrops(SKILLS.FISHING, dropsToAward);

        // Apply Flame-Kissed Mastery double (separate from Fishing doubles)
        if (autoCookDoubled) {
            awardDrops(SKILLS.FISHING, dropsToAward);
        }

        // Apply double fish proc (Well Hooked, Lucky Haul, or Feeding Frenzy)
        if (procs.doubleFish) {
            awardDrops(SKILLS.FISHING, dropsToAward);
            const dropItemId = dropsToAward[0]?.itemId || primaryFishId;
            if (procs.feedingFrenzyUsed) {
                recordTalentProc(SKILLS.FISHING, 'Feeding Frenzy', null, null, { itemId: dropItemId, quantity: 2 });
            } else if (procs.luckyHaul) {
                recordTalentProc(SKILLS.FISHING, 'Lucky Haul', null, null, { itemId: dropItemId, quantity: 2 });
            } else {
                recordTalentProc(SKILLS.FISHING, 'Well Hooked', null, null, { itemId: dropItemId, quantity: 2 });
            }
        }

        // Handle bycatch
        if (procs.bycatch) {
            const bycatchQty = procs.bycatchDouble ? 2 : 1;
            gameState.addItem(procs.bycatch, bycatchQty);
            bountySkill.trackItemProgress(procs.bycatch, bycatchQty);
            recordTalentProc(SKILLS.FISHING, 'Bycatch', null, null, { itemId: procs.bycatch, quantity: bycatchQty });
        }

        // Check for Feeding Frenzy - if we got a double from Well Hooked or Lucky Haul
        // (not from a previous Feeding Frenzy), next catch is guaranteed double
        // feedingFrenzy talent has 100% proc chance when purchased
        if ((procs.luckyHaul || (procs.doubleFish && !procs.feedingFrenzyUsed)) &&
            rollTalentProc(SKILLS.FISHING, 'feedingFrenzy')) {
            this.nextCatchDouble = true;
            recordTalentProc(SKILLS.FISHING, 'Feeding Frenzy Ready', '2x next catch!', null);
        }

        // Check for Perfect Cast proc (instant next catch)
        if (rollTalentProc(SKILLS.FISHING, 'instantCatch')) {
            this.nextCatchInstant = true;
            recordTalentProc(SKILLS.FISHING, 'Perfect Cast', 'Instant next catch!', null);
        }

        // Treasure Nose (Husbandry) - global rare loot bonus
        if (rollGlobalRareLoot()) {
            const treasure = this.getTreasureItem();
            if (treasure.itemId === 'coins') {
                gameState.addGold(treasure.quantity);
                recordTalentProc(SKILLS.FISHING, 'Treasure Nose', null, null, { itemId: 'coins', quantity: treasure.quantity });
            } else {
                gameState.addItem(treasure.itemId, treasure.quantity);
                bountySkill.trackItemProgress(treasure.itemId, treasure.quantity);
                recordTalentProc(SKILLS.FISHING, 'Treasure Nose', null, null, { itemId: treasure.itemId, quantity: treasure.quantity });
            }
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.FISHING);

        // Check for pilferer contract assistance (rolls once per action completion)
        pilferingSkill.tryContractAssist('fishing');

        // Increment stacking speed count (Angler's Rhythm talent)
        incrementStackCount(SKILLS.FISHING, fish.id);

        // Notify UI of completion
        this.onFishingComplete(fish, xpResult);

        return { inventoryFull: dropResult.inventoryFull };
    }

    /**
     * Stop current fishing action
     */
    stopFishing() {
        resetStackCount(SKILLS.FISHING);
        // Reset proc flags when stopping
        this.nextCatchInstant = false;
        this.nextCatchDouble = false;
        gameState.clearAction();
    }

    /**
     * Purchase or equip a rod
     */
    upgradeRod(rodId) {
        const rod = Object.values(RODS).find(r => r.id === rodId);
        if (!rod) return { success: false, reason: 'Invalid rod' };

        const skill = gameState.getSkill(SKILLS.FISHING);
        if (skill.level < rod.level) {
            return { success: false, reason: `Requires level ${rod.level} Fishing` };
        }

        // Check if already owned
        if (gameState.hasPurchasedTool(SKILLS.FISHING, rodId)) {
            // Just equip it
            gameState.equipTool(SKILLS.FISHING, rodId);
            return { success: true, action: 'equipped' };
        }

        // Try to purchase
        const purchaseResult = gameState.purchaseTool(SKILLS.FISHING, rodId, rod.price);
        if (!purchaseResult.success) {
            return purchaseResult;
        }

        // Auto-equip after purchase
        gameState.equipTool(SKILLS.FISHING, rodId);
        return { success: true, action: 'purchased' };
    }

    /**
     * Get available fish for player's level
     */
    getAvailableFish() {
        const skill = gameState.getSkill(SKILLS.FISHING);
        return Object.values(FISH).filter(fish => fish.level <= skill.level);
    }

    /**
     * Callback for when fishing completes (override in UI)
     */
    onFishingComplete(fish, xpResult) {
        logger.game(`Caught ${fish.name}! +${fish.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Fishing level up! Now level ${xpResult.newLevel}`);
        }
    }
}

// Singleton instance
export const fishingSkill = new FishingSkill();
export default fishingSkill;